import React, { useMemo } from 'react';
import { useStore } from '../hooks/useThemeStore';
import { getClassNames } from '../utils';

type ThemeVars = Record<string, string | number>;
type WithClassName = {
    className?: string;
}

const updateStyle = (component: string, vars: ThemeVars | null = null) => {
    if (!vars || Object.keys(vars).length === 0) {
        return undefined;
    }

    const className = `${component}-cst`;
    const stylesInner = Object.keys(vars).reduce((acc, key) => `${acc}${key}: ${vars[key]};`, '');
    const styles = `.${className} {${stylesInner}}`;
    const styleExisting = document.querySelector(`style[data-component-class='${className}']`);

    if (styleExisting && styleExisting.innerHTML === styles) {
        return className;
    }

    const style = (styleExisting || document.createElement('style')) as HTMLStyleElement;

    style.type = 'text/css';
    style.dataset.componentClass = className;
    style.innerHTML = styles;
    document.getElementsByTagName('head')[0].appendChild(style);

    return className;
};

const withTheme = <T extends object>(Component: React.ComponentType<T>) => {
    const Wrapped = (props: T & WithClassName) => {
        const store = useStore();
        const componentName = Component.displayName || Component.name;
        const vars = store?.state?.[componentName] ?? null;
        const classNameCustom = useMemo(() => updateStyle(componentName, vars), [componentName, vars]);
        const className = useMemo(() => getClassNames(classNameCustom, props.className),
            [classNameCustom, props.className]);
        const ComponentWithClassName = Component as React.ComponentType<T & WithClassName>;

        return <ComponentWithClassName {...props} className={className} />;
    };

    return Wrapped;
};

export default withTheme;
